/**
 * OPFS-based blob storage with upload queue.
 * - Blobs are stored locally in OPFS until uploaded
 * - Upload progress tracked in SQLite
 * - Automatic upload when online
 */

import { getSqlocal } from "./db";

const OPFS_DIR = "biostream-blobs";
const API_URL = import.meta.env.VITE_API_URL || "";
const MAX_UPLOAD_ATTEMPTS = 5;

// Listeners notified when a new blob is stored
type BlobStoredListener = (sha256: string) => void;
const blobStoredListeners = new Set<BlobStoredListener>();

/**
 * Register a listener for when new blobs are stored.
 * Used by sync engine to trigger immediate upload.
 */
export function onBlobStored(listener: BlobStoredListener): () => void {
  blobStoredListeners.add(listener);
  return () => blobStoredListeners.delete(listener);
}

// ─── OPFS helpers ──────────────────────────────────────────────────────

async function getOpfsRoot(): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory();
  return await root.getDirectoryHandle(OPFS_DIR, { create: true });
}

/** Extract pure ArrayBuffer from Uint8Array (handles SharedArrayBuffer) */
function toArrayBuffer(data: Uint8Array): ArrayBuffer {
  return data.buffer.slice(
    data.byteOffset,
    data.byteOffset + data.byteLength,
  ) as ArrayBuffer;
}

// ─── OPFS write: createWritable with worker fallback ─────────────────
// Safari may lack createWritable on OPFS handles.  Fall back to an
// inline worker that opens the file by path and uses createSyncAccessHandle.
let _opfsWriteFallback: ((dir: string, name: string, buf: ArrayBuffer) => Promise<void>) | null = null;

function getOpfsWriteFallback() {
  if (_opfsWriteFallback) return _opfsWriteFallback;
  const code = `self.onmessage=async e=>{try{
    const r=await navigator.storage.getDirectory();
    const d=await r.getDirectoryHandle(e.data.dir,{create:true});
    const f=await d.getFileHandle(e.data.name,{create:true});
    const h=await f.createSyncAccessHandle();
    h.truncate(0);h.write(e.data.buf);h.flush();h.close();
    self.postMessage({ok:true});
  }catch(err){self.postMessage({ok:false,error:err.message})}}`;
  const w = new Worker(URL.createObjectURL(new Blob([code], { type: "text/javascript" })));
  let seq = Promise.resolve();
  _opfsWriteFallback = (dir, name, buf) => {
    const p = seq.then(
      () =>
        new Promise<void>((resolve, reject) => {
          const handler = (e: MessageEvent) => {
            w.removeEventListener("message", handler);
            e.data.ok ? resolve() : reject(new Error(e.data.error));
          };
          w.addEventListener("message", handler);
          w.postMessage({ dir, name, buf }, [buf]);
        }),
    );
    seq = p.catch(() => {});
    return p;
  };
  return _opfsWriteFallback;
}

async function opfsWrite(sha256: string, data: Uint8Array): Promise<void> {
  const buf = toArrayBuffer(data);
  const dir = await getOpfsRoot();
  const file = await dir.getFileHandle(sha256, { create: true });
  if (typeof file.createWritable === "function") {
    const writable = await file.createWritable();
    await writable.write(new Blob([buf]));
    await writable.close();
  } else {
    await getOpfsWriteFallback()(OPFS_DIR, sha256, buf);
  }
}

async function opfsRead(sha256: string): Promise<Uint8Array | null> {
  try {
    const dir = await getOpfsRoot();
    const file = await dir.getFileHandle(sha256);
    const blob = await file.getFile();
    return new Uint8Array(await blob.arrayBuffer());
  } catch {
    return null;
  }
}

export async function opfsDelete(sha256: string): Promise<void> {
  try {
    const dir = await getOpfsRoot();
    await dir.removeEntry(sha256);
  } catch {
    // Already deleted or doesn't exist
  }
}

async function opfsExists(sha256: string): Promise<boolean> {
  try {
    const dir = await getOpfsRoot();
    await dir.getFileHandle(sha256);
    return true;
  } catch {
    return false;
  }
}

// ─── SHA256 helper ─────────────────────────────────────────────────────

async function computeSha256(data: Uint8Array): Promise<string> {
  const hashBuffer = await crypto.subtle.digest("SHA-256", toArrayBuffer(data));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ─── Public API ────────────────────────────────────────────────────────

export interface PendingBlob {
  sha256: string;
  mimeType: string;
  size: number;
  createdAt: number;
  uploadAttempts: number;
  lastAttemptAt: number | null;
  error: string | null;
}

/**
 * Store a blob locally in OPFS and queue for upload.
 * Returns the SHA256 hash.
 */
export async function storeBlob(
  data: Uint8Array,
  mimeType: string,
): Promise<string> {
  const sha256 = await computeSha256(data);

  // Check if already exists locally or is already uploaded
  if (await opfsExists(sha256)) {
    return sha256;
  }

  // Write to OPFS
  await opfsWrite(sha256, data);

  // Add to upload queue (ignore if already queued)
  await getSqlocal().sql`
    INSERT OR IGNORE INTO pending_blobs (sha256, mime_type, size, created_at)
    VALUES (${sha256}, ${mimeType}, ${data.length}, ${Date.now()})
  `;

  // Notify listeners (for immediate upload)
  for (const listener of blobStoredListeners) {
    try {
      listener(sha256);
    } catch {}
  }

  return sha256;
}

/**
 * Get a blob - first checks OPFS, then fetches from server and caches locally.
 */
export async function getBlob(
  sha256: string,
): Promise<{ data: Uint8Array; mimeType: string } | null> {
  // Check OPFS first
  const local = await opfsRead(sha256);
  if (local) {
    // Get mime type from pending_blobs if available
    const rows = (await getSqlocal().sql`
      SELECT mime_type FROM pending_blobs WHERE sha256 = ${sha256}
    `) as { mime_type: string }[];
    return {
      data: local,
      mimeType: rows[0]?.mime_type || "application/octet-stream",
    };
  }

  // Fetch from server
  try {
    const res = await fetch(`${API_URL}/blobs/${sha256}`);
    if (!res.ok) return null;
    const data = new Uint8Array(await res.arrayBuffer());
    const mimeType =
      res.headers.get("content-type") || "application/octet-stream";
    // Cache in OPFS for offline access
    try {
      await opfsWrite(sha256, data);
    } catch {}
    return { data, mimeType };
  } catch {
    return null;
  }
}

/**
 * Get list of pending blob uploads.
 */
export async function getPendingBlobs(): Promise<PendingBlob[]> {
  const rows = (await getSqlocal().sql`
    SELECT sha256, mime_type, size, created_at, upload_attempts, last_attempt_at, error
    FROM pending_blobs
    ORDER BY created_at ASC
  `) as any[];

  return rows.map((r) => ({
    sha256: r.sha256,
    mimeType: r.mime_type,
    size: r.size,
    createdAt: r.created_at,
    uploadAttempts: r.upload_attempts,
    lastAttemptAt: r.last_attempt_at,
    error: r.error,
  }));
}

/**
 * Get count of pending uploads.
 */
export async function getPendingBlobCount(): Promise<number> {
  const rows = (await getSqlocal()
    .sql`SELECT COUNT(*) as count FROM pending_blobs`) as { count: number }[];
  return rows[0]?.count ?? 0;
}

/**
 * Upload a single pending blob to the server.
 * Returns true if successful.
 */
export async function uploadBlob(sha256: string): Promise<boolean> {
  const data = await opfsRead(sha256);
  if (!data) {
    // Blob missing from OPFS - remove from queue
    await getSqlocal().sql`DELETE FROM pending_blobs WHERE sha256 = ${sha256}`;
    return false;
  }

  // Get mime type
  const rows = (await getSqlocal().sql`
    SELECT mime_type FROM pending_blobs WHERE sha256 = ${sha256}
  `) as { mime_type: string }[];
  const mimeType = rows[0]?.mime_type || "application/octet-stream";

  // Update attempt count
  await getSqlocal().sql`
    UPDATE pending_blobs
    SET upload_attempts = upload_attempts + 1, last_attempt_at = ${Date.now()}, error = NULL
    WHERE sha256 = ${sha256}
  `;

  try {
    const res = await fetch(`${API_URL}/blobs/${sha256}`, {
      method: "POST",
      headers: { "Content-Type": mimeType },
      body: new Blob([toArrayBuffer(data)]),
    });

    if (res.ok) {
      // Success - remove from upload queue (keep in OPFS as cache)
      await getSqlocal()
        .sql`DELETE FROM pending_blobs WHERE sha256 = ${sha256}`;
      return true;
    } else {
      throw new Error(`HTTP ${res.status}`);
    }
  } catch (e: any) {
    // Record error
    await getSqlocal().sql`
      UPDATE pending_blobs SET error = ${e.message || "Upload failed"} WHERE sha256 = ${sha256}
    `;
    return false;
  }
}

/**
 * Upload all pending blobs. Called by sync engine when online.
 * Returns number of successful uploads.
 */
export async function uploadAllPending(): Promise<number> {
  const pending = await getPendingBlobs();
  let uploaded = 0;

  for (const blob of pending) {
    // Skip if too many attempts
    if (blob.uploadAttempts >= MAX_UPLOAD_ATTEMPTS) {
      continue;
    }

    // Add exponential backoff
    if (blob.lastAttemptAt) {
      const backoff = Math.min(1000 * Math.pow(2, blob.uploadAttempts), 60000);
      if (Date.now() - blob.lastAttemptAt < backoff) {
        continue;
      }
    }

    if (await uploadBlob(blob.sha256)) {
      uploaded++;
    }
  }

  return uploaded;
}

/**
 * Clean up blobs that have exceeded max attempts.
 * Called periodically to prevent queue from growing indefinitely.
 */
export async function cleanupFailedBlobs(): Promise<number> {
  const rows = (await getSqlocal().sql`
    SELECT sha256 FROM pending_blobs WHERE upload_attempts >= ${MAX_UPLOAD_ATTEMPTS}
  `) as { sha256: string }[];

  for (const row of rows) {
    await opfsDelete(row.sha256);
  }

  await getSqlocal()
    .sql`DELETE FROM pending_blobs WHERE upload_attempts >= ${MAX_UPLOAD_ATTEMPTS}`;
  return rows.length;
}

/**
 * Get total size of pending uploads in bytes.
 */
export async function getPendingBlobSize(): Promise<number> {
  const rows = (await getSqlocal()
    .sql`SELECT SUM(size) as total FROM pending_blobs`) as { total: number }[];
  return rows[0]?.total ?? 0;
}

/**
 * Get the server URL for a blob.
 */
export function getBlobServerUrl(sha256: string): string {
  return `${API_URL}/blobs/${sha256}`;
}

/**
 * Get a displayable URL for a blob - checks OPFS first, then fetches from
 * server and caches locally for offline access.
 * Returns object URL. Caller is responsible for revoking when done.
 * @param mimeType - optional MIME type hint for correct Blob construction
 */
export async function getBlobUrl(
  sha256: string,
  mimeType?: string,
): Promise<string> {
  const blobOpts = mimeType ? { type: mimeType } : undefined;
  // Check OPFS first
  const local = await opfsRead(sha256);
  if (local) {
    return URL.createObjectURL(new Blob([toArrayBuffer(local)], blobOpts));
  }
  // Fetch from server, cache in OPFS, return object URL
  try {
    const res = await fetch(`${API_URL}/blobs/${sha256}`);
    if (res.ok) {
      const data = new Uint8Array(await res.arrayBuffer());
      const serverMime = res.headers.get("content-type") || mimeType;
      try {
        await opfsWrite(sha256, data);
      } catch {}
      return URL.createObjectURL(
        new Blob(
          [toArrayBuffer(data)],
          serverMime ? { type: serverMime } : undefined,
        ),
      );
    }
  } catch {}
  // Last resort: direct server URL (no caching, won't work offline)
  return `${API_URL}/blobs/${sha256}`;
}
